import moment from 'moment'
import { Set } from 'immutable'

import { selectableCards } from 'src/helpers'
import {
  CardStatus,
  getMarketingContent,
  getPaginatedRecipients,
  getSentCards,
  ProductionInfoFragment,
  RecipientFragment,
} from 'src/graphql'
import {
  ContactSearchFilter,
  DateSearchFilter,
  SearchFilter,
  SearchFilterType,
} from 'src/chrome/FilteredSearchBar/types'
import {
  CardHistoryEditCardRoute,
  CardHistoryResendCardRoute,
} from 'src/dashboard/routes/CardHistoryRoute'
import {
  FormContact,
  NonNullableAttributes,
} from 'src/contacts/components/AddressForm/AddressForm'

import { ProductionRecipient, Props, Section, Step, Steps } from './types'

import {
  useActions,
  useEffect,
  useMemo,
  useMutations,
  useQueries,
  useSelector,
  useState,
} from 'src/hooks'

export const PAGE_SIZE = 20

const filtersFactory = (filters?: SearchFilter[]): SearchFilter[] => {
  const contactFilter = filters?.find(
    filter =>
      filter.type === SearchFilterType.CONTACT && filter.name === 'Recipient',
  ) as ContactSearchFilter | undefined
  const dateFilter = filters?.find(
    filter =>
      filter.type === SearchFilterType.DATERANGE && filter.name === 'Recipient',
  ) as DateSearchFilter | undefined

  return [
    {
      type: SearchFilterType.CONTACT,
      name: 'Recipient',
      value: contactFilter?.value,
    },
    {
      type: SearchFilterType.DATERANGE,
      name: 'Date',
      value: dateFilter?.value,
    },
  ]
}

export default function Hooks(props: Props) {
  const { initialSection, initialFilters } = props

  const actions = useActions()

  const [filters, setFilters] = useState(filtersFactory(initialFilters))
  const mutations = useMutations()
  const [step, setStep] = useState<Step>({ type: Steps.Idle })
  const resetStep = () => setStep({ type: Steps.Idle })
  const showError = (message: string) => setStep({ type: Steps.Error, message })
  const [section, setSection] = useState<Section>(initialSection ?? 'All')
  const [currentPage, setCurrentPage] = useState(1)
  const [selected, setSelected] = useState(Set())
  const [terms, setTerms] = useState('')

  const filterValues = useMemo(
    () =>
      filters.reduce(
        (
          accum: {
            startDate?: moment.Moment
            endDate?: moment.Moment
            contactIds: string[]
          },
          filter,
        ) => {
          if (filter.type === SearchFilterType.CONTACT && filter.value) {
            return {
              ...accum,
              contactIds: [
                ...accum.contactIds,
                ...filter.value.map(contact => contact.id),
              ],
            } // change if we want to have more than one filter value per type
          }

          if (filter.type === SearchFilterType.DATERANGE && filter.value) {
            return {
              ...accum,
              startDate: filter.value.start,
              endDate: filter.value.end,
            }
          }

          return accum
        },
        {
          startDate: undefined,
          endDate: undefined,
          contactIds: Array<string>(),
        },
      ),
    [filters],
  )

  const getStatusForRequest = (
    radioSelected: string,
  ): CardStatus | undefined => {
    const selected = radioSelected.toUpperCase()
    switch (selected) {
      case 'FULFILLED':
        return CardStatus.FULFILLED
      case 'HELD':
        return CardStatus.HELD
      case 'PENDING':
        return CardStatus.PENDING
      case 'REJECTED':
        return CardStatus.REJECTED
      case 'PAYMENT_ERROR':
        return CardStatus.PAYMENT_ERROR
      default:
        return undefined
    }
  }

  const context = section === 'Payment Error' ? 'paused' : 'sent'

  const [cardHistory, recipientHistory, marketingCopy] = useQueries(
    context === 'sent'
      ? getSentCards({
          page: currentPage,
          search: terms,
          contacts: filterValues.contactIds,
          status: getStatusForRequest(section),
          startDate: filterValues.startDate?.toISOString(),
          endDate: filterValues.endDate?.toISOString(),
        })
      : undefined,
    context === 'paused'
      ? getPaginatedRecipients({
          paymentFailures: true,
          page: currentPage,
          search: terms,
          contacts: filterValues.contactIds,
        })
      : undefined,
    getMarketingContent(),
  )

  const run = async (task: () => Promise<void>, message: string) => {
    setStep({
      type: Steps.Progress,
      message,
    })

    try {
      await task()
    } catch (error) {
      showError(error.toString())
    } finally {
      resetStep()
    }
  }

  const editSendDate = (info: ProductionInfoFragment) => {
    setStep({ type: Steps.SetDate, info })
  }

  const saveSendDate = async (startDate?: moment.Moment) => {
    if (step.type !== Steps.SetDate) return
    if (!startDate) return

    run(async () => {
      await mutations.updateProductionInfo({
        input: {
          dateToSend: startDate.format('YYYY-MM-DD'),
          productionId: step.info.id,
        },
      })
    }, 'Updating Send Date...')
  }

  const editRecipient = (recipient: RecipientFragment) => {
    setStep({ type: Steps.EditRecipient, recipient })
  }

  const saveRecipient = async (
    address: NonNullableAttributes<FormContact>,
    updateContactToo: boolean,
    userVerified: boolean,
  ) => {
    if (step.type !== Steps.EditRecipient || !step.recipient.address) {
      return
    }

    run(async () => {
      const recipient = {
        ...step.recipient,
        address: {
          ...step.recipient.address,
          ...address,
        },
      }

      await mutations.updateRecipient({
        recipient,
        updateContactToo,
        userOverrideInvalidAddress: userVerified,
      })
    }, 'Updating Recipient...')
  }

  const viewCard = async (recipient: ProductionRecipient) => {
    setStep({ type: Steps.View, recipient })

    try {
      if (!recipient.card) return
      const {
        getOrCreateEditableCardForRecipient: { card },
      } = await mutations.getOrCreateEditableCardForRecipient({
        recipientId: recipient.id,
        productionId: recipient.productionId,
      })
      setStep({ type: Steps.View, recipient, card })
    } catch (error) {
      showError(error.toString())
    }
  }

  const isMobile = useSelector(state => state.window.isMobile)

  const handleOrderResentCard = async (cardId: string) => {
    setStep({ type: Steps.Progress, message: 'Creating your order...' })

    try {
      const result = await mutations.createOrder({
        order: {
          lines: [
            {
              card: cardId,
            },
          ],
        },
      })

      actions.openOrder(result.createOrder.order.id)
    } catch (error) {
      showError(error.toString())
    }
  }

  const goToIndex = () => {
    actions.openCardHistory(section, filters)
  }

  const onResendCard = async (id: string) => {
    setStep({ type: Steps.Progress, message: 'Building copy of card...' })

    try {
      const result = await mutations.createCard({ card: id })

      if (result.createCard.card) {
        actions.openCardHistory(
          section,
          filters,
          CardHistoryResendCardRoute(result.createCard.card.id),
        )
      }
    } catch (error) {
      showError(error.toString())
    }
  }

  const onConfirmCancel = () => {
    switch (context) {
      case 'paused':
        return onConfirmRetry()
      case 'sent':
        return onConfirmCancelPending()
    }
  }

  const onConfirmCancelPending = () => {
    setStep({
      type: Steps.CancelCards,
      userConfirmed: false,
      ids: [...selected.values()],
    })
  }

  const onCancelPending = async (ids: string[]) => {
    setStep({ type: Steps.CancelCards, ids, userConfirmed: true })

    try {
      const result = await mutations.cancelPendingCards({ productionIds: ids })

      if (result.cancelPendingCards.refundInfo) {
        setStep({
          type: Steps.CancelCardsSummary,
          ids,
          refundInfo: result.cancelPendingCards.refundInfo,
        })
      }
      setSelected(Set())
    } catch (error) {
      resetStep()
      showError('Sorry, could not cancel cards, please try again later')
    }
  }

  const onConfirmRetry = () => {
    setStep({
      type: Steps.RetryFailedCards,
      ids: [...selected],
      userConfirmed: false,
    })
  }

  const onRetry = async () => {
    setStep({
      type: Steps.RetryFailedCards,
      ids: [...selected],
      userConfirmed: true,
    })

    try {
      await mutations.sendPausedRecipients({
        recipientIds: Array.from(selected),
      })
      setSelected(Set())
    } catch (error) {
      showError('There was an error canceling a card, please try again later')
    }
  }

  const handleEditCard = async (recipientId: string) => {
    const {
      getOrCreateEditableCardForRecipient: {
        card: { id },
      },
    } = await mutations.getOrCreateEditableCardForRecipient({ recipientId })
    actions.openCardHistory(section, filters, CardHistoryEditCardRoute(id))
  }

  const handleCardUpdated = async () => {
    setStep({ type: Steps.Idle })
    actions.openCardHistory(section, filters)
  }

  const displayedData = (() => {
    switch (context) {
      case 'paused':
        return recipientHistory
      case 'sent':
        return cardHistory
    }
  })()

  const isSelectAllChecked = (() => {
    if (context !== 'sent' || !cardHistory) {
      return false
    }

    if (cardHistory.count === 0) {
      return false
    }

    return cardHistory.results
      .filter(selectableCards)
      .map(card => card.id)
      .every(id => selected.has(id))
  })()

  const handleSelect = (id: string) => {
    const newSet = selected.has(id) ? selected.delete(id) : selected.add(id)
    setSelected(newSet)
  }

  const handleSelectAll = () => {
    const originalSelection = selected

    if (isSelectAllChecked) {
      setSelected(Set())
      return
    }

    const selectable: Array<{ id: string }> | undefined =
      context === 'sent'
        ? cardHistory?.results.filter(selectableCards)
        : recipientHistory?.results
    const historyIds = selectable?.map(item => item.id) ?? []
    const newSelection = originalSelection.union(Set(historyIds))

    setSelected(newSelection)
  }

  const selectName = isSelectAllChecked ? 'Deselect All' : 'Select All'

  useEffect(() => {
    setSelected(Set())
  }, [context])

  useEffect(() => {
    setCurrentPage(1)
  }, [filters, terms])

  return {
    isSelectAllChecked,
    cardHistory,
    context: context as typeof context,
    currentPage,
    displayedData,
    editRecipient,
    editSendDate,
    filters,
    handleEditCard,
    handleSelect,
    handleSelectAll,
    isMobile,
    marketingCopy,
    onCancelPending,
    onConfirmCancel,
    onConfirmRetry,
    onResendCard,
    onRetry,
    recipientHistory,
    resetStep,
    saveRecipient,
    saveSendDate,
    section,
    selected,
    selectName: selectName as typeof selectName,
    setCurrentPage,
    setFilters,
    setSection,
    setTerms,
    step,
    viewCard,
    handleCardUpdated,
    handleOrderResentCard,
    goToIndex,
  }
}
